<template>
  <div
    class="card"
    :style="`min-height: ${minHeight}px;`"
  >
    <header class="card__header" data-id="cardHeader">
      <h2 class="card__title">{{ $t('Common.latestRides') }}</h2>
      <router-link
        v-if="isSuccess"
        :to="route"
        :title="$t('Common.seeMore')"
        class="card__action"
      >
        <span>{{ $t('Common.seeMore') }}</span>
        <v-icon>add</v-icon>
      </router-link>
    </header>

    <div v-if="isSuccess">
      <div v-if="items && items.length"
        class="card__body"
        :style="`height: ${cardBodyHeight}px;`"
        @scroll="loadMore"
      >
        <ul class="rides-list">
          <li
            v-for="(item, index) in items"
            :key="index"
            class="rides-list__item"
          >
            <span
              class="rides-list__item--employee"
              @click="getRideData(item)"
            >
              {{ item.employee.name }}
            </span>
            <span class="rides-list__item--cc">{{ item.costCenter.name }}</span>
            <p class="rides-list__item--text">{{ $t('Common.finishedRide') }} {{ item.costCenter.taxiCategoryName }} {{ $t('Common.of') }} {{ item.odometer | metresToKm }} {{ $t('Common.per') }} {{ item.fare | currencyBRL }} ({{ item.start.date | durationCalcPerExtensive(item.end.date) }} {{ $t('Common.of') }} <span class="text-lowercase">{{ $t('Common.duration') }}</span>).</p>
          </li>
        </ul>

        <!-- loading -->
        <transition name="fade">
          <div
            v-if="isFetchingRides"
            class="text-xs-center py-4"
          >
            <v-progress-circular indeterminate color="accent" />
          </div>
        </transition>
      </div>

      <v-layout
        v-if="items && !items.length"
        class="card__content text-xs-center"
        :style="`height: ${cardBodyHeight}px;`"
        align-center
        justify-center
        row
        fill-height
      >
        <div class="card--message px-4">
          <p><strong v-html="$t('Views.HomePage.latestRidesEmpty')" /></p>
          <p>
            <span v-html="$t('Views.HomePage.latestRidesHelp')" /> <a href="https://99empresas.zendesk.com/hc/pt-br" :title="$t('Common.clickHere')" target="_blank" class="text--link accent--text">{{ $t('Common.clickHere') }}</a>&nbsp;<span v-html="$t('Views.HomePage.andKnowHow')" />
          </p>
        </div>
      </v-layout>
    </div>

    <v-layout
      v-if="isFailed"
      class="card__content card--message text-xs-center"
      :style="`height: ${cardBodyHeight}px;`"
      align-center
      justify-center
      row
      fill-height
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="getData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </v-layout>

    <v-layout
      v-if="isLoading || !items"
      class="card__content text-xs-center"
      :style="`height: ${cardBodyHeight}px;`"
      align-center
      justify-center
      row
      fill-height
    >
      <v-progress-circular indeterminate color="accent" />
    </v-layout>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'

export default {
  name: 'LatestRides',
  props: {
    route: {
      type: Object
    },
    filters: {
      type: Object,
      default: () => {}
    },
    minHeight: {
      type: Number,
      default: 0
    }
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },

    cardBodyHeight () {
      const cardHeader = document.querySelector('[data-id="cardHeader"]')
      return !cardHeader ? this.minHeight : this.minHeight - cardHeader.offsetHeight
    }
  },
  data () {
    return {
      namespace: 'Components.LatestRides',
      currentStatus: STATUS_IS_LOADING,
      items: [],
      page: 1,
      limit: 25,
      isFetchingRides: false,
      isLastPage: false
    }
  },
  methods: {
    getRideData (data) {
      this.$emit('getRideData', data)
    },

    resetItems () {
      this.items = null
      this.page = 1
      this.limit = 25
      this.isFetchingRides = false
      this.isLastPage = false
    },

    async getData () {
      try {
        const page = this.page
        const limit = this.limit
        const startDate = this.filters.startDate || null
        const endDate = this.filters.endDate || null
        const companyId = this.company ? this.company.id : null
        const { data: res } = await this.$apiService.getRides(
          { page },
          { limit },
          { startDate },
          { endDate },
          { companyId },
          { orderBy: 'desc' }
        )

        if (!res.data.length) {
          this.isLastPage = true
        }

        this.items = !this.items
          ? res.data
          : this.items.concat(res.data)

        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('Error', err)
        this.currentStatus = STATUS_FAILED
      }

      this.isFetchingRides = false
    },

    loadMore (event) {
      if (this.isLastPage || this.isFetchingRides) return

      const wrapper = event.target
      if (wrapper.offsetHeight + wrapper.scrollTop === wrapper.scrollHeight) {
        this.page += 1
        this.getData()
        this.isFetchingRides = true
      }
    }
  },
  watch: {
    activeOption () {
      this.forceRenderChart()
    },

    filters: {
      deep: true,
      handler () {
        this.resetItems()
        this.getData()
      }
    },

    company () {
      this.resetItems()
      this.getData()
    }
  },
  mounted () {
    this.resetItems()
    this.getData()
  }
}
</script>

<style lang="scss" scoped>
.card {
  background-color: #fff;
  margin: 12px;
}

.card__title {
  padding-bottom: 12px;
  padding-top: 12px;
}

.card__body {
  height: 230px;
  overflow: auto;
  padding-top: 0;
}

.card__content {
  min-height: 230px;
}

.rides-list {
  list-style: none;
  padding-left: 0;
}

.rides-list__item {
  border-bottom: 1px solid $gray-4;
  padding: 16px 0;

  &:last-child {
    border-bottom: none;
  }
}

.rides-list__item--employee {
  color: $accent;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.rides-list__item--cc,
.rides-list__item--text {
  font-size: 12px;
}

.rides-list__item--cc {
  display: block;
}

.rides-list__item--text {
  margin-bottom: 0;
}
</style>
